import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { type ColumnDef } from "@tanstack/react-table";
import { IconExternalLink } from "@tabler/icons-react";

import PageHeader from "../../components/common/PageHeader";
import { productService } from "../../services/product.service";
import { MarginCalculator } from "../../components/brand/MarginCalculator";
import { DataTable, DataTablePagination } from "../../components/ui/data-table";
import { UnderlineTabs } from "@/components/ui/underline-tabs";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { DashboardCard } from "@/components/dashboard/dashboard-card";
import { IconButton } from "@/components/dashboard/icon-button";
import {
  describeMonthChange,
  MetricPanel,
  MetricStrip,
  MetricStripSkeleton,
  type Metric,
} from "@/components/dashboard/metric-cell";
import type { RenewalRow } from "@/types/products-analytics";
import { formatCommission, ProductIdentity } from "@/components/ProductTable/ProductTable";
import { formatAmount, formatDate, formatNumber } from "@/utils/formatters";

interface ProductStats {
  affiliateSalesCountTotal: number;
  affiliateSalesCountThisMonth: number;
  campaignSalesCountTotal: number;
  campaignSalesCountThisMonth: number;
}

interface ProductVariant {
  id: string;
  title: string;
  image: string;
  price: string;
  inventory: number;
  program: {
    id: string;
    commissionType: string;
    commissionValue: string;
  };
  stats: ProductStats;
}

interface Product {
  id: string;
  title: string;
  description: string;
  image: string;
  stats: ProductStats;
}

interface SalesMonthlyData {
  year: number;
  month: number;
  affiliate: number;
  campaign: number;
  total: number;
}

interface SalesRecord {
  id: string;
  orderId: string;
  orderNumber: string;
  totalAmount: string;
  commissionAmount?: string;
  totalDiscount?: string;
  codeUsed: string;
  orderStatusUrl: string;
  createdAt: string;
}

interface SalesResponse {
  data: SalesRecord[];
  pagination: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  };
}

type SalesTab = "affiliate" | "campaign" | "variants" | "recurring";

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/** "2026-02" → "Feb 2026" */
function monthLabel(key: string) {
  const [year, month] = key.split("-");
  return `${MONTHS[Number(month) - 1] ?? month} ${year}`;
}

function buildSalesColumns(amountColumn: ColumnDef<SalesRecord>): ColumnDef<SalesRecord>[] {
  return [
    {
      accessorKey: "orderNumber",
      header: "Order",
      meta: { width: "w-[14%]" },
      cell: ({ row }) => (
        <span className="font-medium tabular-nums text-foreground">#{row.original.orderNumber}</span>
      ),
    },
    {
      accessorKey: "totalAmount",
      header: "Order total",
      meta: { width: "w-[20%]" },
      cell: ({ row }) => (
        <span className="tabular-nums text-foreground">
          {formatAmount(Number(row.original.totalAmount) || 0)}
        </span>
      ),
    },
    amountColumn,
    {
      accessorKey: "codeUsed",
      header: "Code",
      meta: { width: "w-[22%]" },
      cell: ({ row }) => <span className="text-foreground">{row.original.codeUsed}</span>,
    },
    {
      accessorKey: "createdAt",
      header: "Date",
      meta: { width: "w-[18%]" },
      cell: ({ row }) => (
        <span className="whitespace-nowrap text-muted-foreground">
          {formatDate(row.original.createdAt)}
        </span>
      ),
    },
    {
      id: "open",
      header: "",
      meta: { width: "w-[6%]", cellClassName: "text-right", skeleton: <span /> },
      cell: ({ row }) => (
        <IconButton
          label="Open order in Shopify"
          icon={<IconExternalLink stroke={1.75} />}
          onClick={() => window.open(row.original.orderStatusUrl, "_blank", "noopener,noreferrer")}
        />
      ),
    },
  ];
}

const affiliateSalesColumns = buildSalesColumns({
  accessorKey: "commissionAmount",
  header: "Commission",
  meta: { width: "w-[20%]" },
  cell: ({ row }) => (
    <span className="tabular-nums text-foreground">
      {formatAmount(Number(row.original.commissionAmount) || 0)}
    </span>
  ),
});

/** Recharge renewals that carried this product: who earned, on what charge. */
const renewalColumns: ColumnDef<RenewalRow>[] = [
  {
    accessorKey: "orderNumber",
    header: "Order",
    meta: { width: "w-[14%]" },
    cell: ({ row }) => (
      <span className="font-medium tabular-nums text-foreground">#{row.original.orderNumber}</span>
    ),
  },
  {
    accessorKey: "affiliateName",
    header: "Affiliate",
    meta: { width: "w-[26%]" },
    cell: ({ row }) => (
      <span className="block max-w-[14rem] truncate text-foreground" title={row.original.affiliateName}>
        {row.original.affiliateName}
      </span>
    ),
  },
  {
    accessorKey: "amount",
    header: "Charge total",
    meta: { width: "w-[18%]" },
    cell: ({ row }) => (
      <span className="tabular-nums text-foreground">{formatAmount(row.original.amount)}</span>
    ),
  },
  {
    accessorKey: "commission",
    header: "Commission",
    meta: { width: "w-[18%]" },
    cell: ({ row }) => (
      <span className="tabular-nums text-foreground">{formatAmount(row.original.commission)}</span>
    ),
  },
  {
    accessorKey: "createdAt",
    header: "Date",
    meta: { width: "w-[18%]" },
    cell: ({ row }) => (
      <span className="whitespace-nowrap text-muted-foreground">{formatDate(row.original.createdAt)}</span>
    ),
  },
  {
    id: "open",
    header: "",
    meta: { width: "w-[6%]", cellClassName: "text-right", skeleton: <span /> },
    cell: ({ row }) =>
      row.original.orderStatusUrl ? (
        <IconButton
          label="Open order in Shopify"
          icon={<IconExternalLink stroke={1.75} />}
          onClick={() => window.open(row.original.orderStatusUrl!, "_blank", "noopener,noreferrer")}
        />
      ) : null,
  },
];

const campaignSalesColumns = buildSalesColumns({
  accessorKey: "totalDiscount",
  header: "Discount",
  meta: { width: "w-[20%]" },
  cell: ({ row }) => (
    <span className="tabular-nums text-foreground">
      {formatAmount(Number(row.original.totalDiscount) || 0)}
    </span>
  ),
});

function InsightsSkeleton() {
  return (
    <div className="space-y-4">
      <div className="animate-pulse space-y-3">
        <div className="h-4 w-16 rounded bg-muted" />
        <div className="h-7 w-64 rounded bg-muted" />
      </div>
      <div className="rounded-lg border border-border bg-card">
        <div className="flex items-center gap-4 px-5 py-4 animate-pulse">
          <div className="size-14 rounded-md bg-muted" />
          <div className="space-y-2">
            <div className="h-4 w-48 rounded bg-muted" />
            <div className="h-3 w-72 rounded bg-muted" />
          </div>
        </div>
        <MetricStripSkeleton count={5} columns="lg:grid-cols-5" />
      </div>
      <div className="rounded-lg border border-border bg-card">
        <div className="flex items-center justify-between px-5 pt-4 pb-3 animate-pulse">
          <div className="h-4 w-14 rounded bg-muted" />
        </div>
        <div className="mx-5 flex gap-6 border-b border-border pb-2 animate-pulse">
          <div className="h-3.5 w-24 rounded bg-muted" />
          <div className="h-3.5 w-24 rounded bg-muted" />
          <div className="h-3.5 w-16 rounded bg-muted" />
        </div>
        <div className="px-3 pb-3">
          <DataTable columns={affiliateSalesColumns} data={[]} isLoading skeletonRows={6} />
        </div>
      </div>
    </div>
  );
}

export function ProductsInsights() {
  const { productId } = useParams<{ productId: string }>();
  const [product, setProduct] = useState<Product | null>(null);
  const [variants, setVariants] = useState<ProductVariant[]>([]);
  const [monthly, setMonthly] = useState<SalesMonthlyData[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingVariants, setIsLoadingVariants] = useState(false);
  const [endCursor, setEndCursor] = useState<string | null>(null);
  const [hasNextPage, setHasNextPage] = useState(false);
  const [tab, setTab] = useState<SalesTab>("affiliate");
  const [marginVariant, setMarginVariant] = useState<ProductVariant | null>(null);

  const [affiliateSales, setAffiliateSales] = useState<SalesRecord[]>([]);
  const [affiliatePageSize, setAffiliatePageSize] = useState(10);
  const [affiliatePagination, setAffiliatePagination] = useState({
    total: 0,
    page: 1,
    limit: 10,
    totalPages: 0,
  });
  const [isLoadingAffiliateSales, setIsLoadingAffiliateSales] = useState(false);

  const [campaignSales, setCampaignSales] = useState<SalesRecord[]>([]);
  const [campaignPageSize, setCampaignPageSize] = useState(10);
  const [campaignPagination, setCampaignPagination] = useState({
    total: 0,
    page: 1,
    limit: 10,
    totalPages: 0,
  });
  const [isLoadingCampaignSales, setIsLoadingCampaignSales] = useState(false);

  // Recharge renewals for this product. `connected` false hides the tab.
  const recurringQuery = useQuery({
    queryKey: ["product-recurring", productId],
    queryFn: () => productService.getRecurring(productId!),
    enabled: !!productId,
  });
  const recurring = recurringQuery.data;

  useEffect(() => {
    const fetchProductData = async () => {
      if (!productId) return;
      try {
        setIsLoading(true);
        const response = await productService.getVariants(productId);
        setProduct(response.product);
        setVariants(response.variants || []);
        setEndCursor(response.pagination?.endCursor || null);
        setHasNextPage(response.pagination?.hasNextPage || false);
      } catch (error) {
        console.error("Failed to fetch product data:", error);
      } finally {
        setIsLoading(false);
      }
    };
    fetchProductData();
  }, [productId]);

  useEffect(() => {
    const fetchSalesMonthly = async () => {
      if (!productId) return;
      try {
        const response: SalesMonthlyData[] = await productService.getSalesMonthly(productId);
        setMonthly(response);
      } catch (error) {
        console.error("Failed to fetch sales monthly data:", error);
      }
    };
    fetchSalesMonthly();
  }, [productId]);

  useEffect(() => {
    if (productId) {
      fetchAffiliateSales(1);
      fetchCampaignSales(1);
    }
  }, [productId]);

  const handleLoadMoreVariants = async () => {
    if (!productId || !hasNextPage || isLoadingVariants) return;
    try {
      setIsLoadingVariants(true);
      const response = await productService.getVariants(productId, endCursor || undefined);
      setVariants((prev) => [...prev, ...(response.variants || response.data || [])]);
      setEndCursor(response.pagination?.endCursor || null);
      setHasNextPage(response.pagination?.hasNextPage || false);
    } catch (error) {
      console.error("Failed to load more variants:", error);
    } finally {
      setIsLoadingVariants(false);
    }
  };

  const fetchAffiliateSales = async (page: number = 1, limit: number = affiliatePageSize) => {
    if (!productId) return;
    try {
      setIsLoadingAffiliateSales(true);
      const response: SalesResponse = await productService.getAffiliateSales(productId, page, limit);
      setAffiliateSales(response.data);
      setAffiliatePagination(response.pagination);
    } catch (error) {
      console.error("Failed to fetch affiliate sales:", error);
    } finally {
      setIsLoadingAffiliateSales(false);
    }
  };

  const fetchCampaignSales = async (page: number = 1, limit: number = campaignPageSize) => {
    if (!productId) return;
    try {
      setIsLoadingCampaignSales(true);
      const response: SalesResponse = await productService.getCampaignSales(productId, page, limit);
      setCampaignSales(response.data);
      setCampaignPagination(response.pagination);
    } catch (error) {
      console.error("Failed to fetch campaign sales:", error);
    } finally {
      setIsLoadingCampaignSales(false);
    }
  };

  const variantColumns = useMemo<ColumnDef<ProductVariant>[]>(
    () => [
      {
        accessorKey: "title",
        header: "Variant",
        cell: ({ row }) => <ProductIdentity title={row.original.title} image={row.original.image} />,
      },
      {
        accessorKey: "price",
        header: "Price",
        meta: { fit: true },
        cell: ({ row }) => (
          <span className="tabular-nums text-foreground">
            {formatAmount(Number(row.original.price) || 0)}
          </span>
        ),
      },
      {
        id: "affiliateSales",
        header: "Affiliate sales",
        meta: { fit: true },
        cell: ({ row }) => (
          <span className="tabular-nums text-foreground">
            {formatNumber(row.original.stats.affiliateSalesCountTotal)}
          </span>
        ),
      },
      {
        id: "campaignSales",
        header: "Campaign sales",
        meta: { fit: true },
        cell: ({ row }) => (
          <span className="tabular-nums text-foreground">
            {formatNumber(row.original.stats.campaignSalesCountTotal)}
          </span>
        ),
      },
      {
        id: "commission",
        header: "Commission",
        meta: { fit: true },
        cell: ({ row }) => (
          <span className="tabular-nums text-foreground">{formatCommission(row.original.program)}</span>
        ),
      },
      {
        accessorKey: "inventory",
        header: "Stock",
        meta: { fit: true },
        cell: ({ row }) =>
          row.original.inventory > 0 ? (
            <span className="tabular-nums text-foreground">{formatNumber(row.original.inventory)}</span>
          ) : (
            <span className="text-muted-foreground">Out of stock</span>
          ),
      },
      {
        id: "margin",
        header: "",
        meta: { fit: true, cellClassName: "text-right", skeleton: <span /> },
        cell: ({ row }) => (
          <button
            type="button"
            onClick={() => setMarginVariant(row.original)}
            className="whitespace-nowrap rounded-sm text-xs font-medium text-muted-foreground underline-offset-4 transition-colors hover:text-foreground hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            Calculate margin
          </button>
        ),
      },
    ],
    []
  );

  // Monthly units keyed "YYYY-MM" for the chart behind the strip.
  const monthlySeries = useMemo(() => {
    const series: Record<string, number> = {};
    for (const m of monthly) {
      series[`${m.year}-${String(m.month).padStart(2, "0")}`] = m.total;
    }
    return series;
  }, [monthly]);
  const monthKeys = useMemo(() => Object.keys(monthlySeries).sort(), [monthlySeries]);
  const thisMonthKey = monthKeys[monthKeys.length - 1];
  const lastMonthKey = monthKeys[monthKeys.length - 2];
  const stats = product?.stats;
  const unitsTotal = (stats?.affiliateSalesCountTotal ?? 0) + (stats?.campaignSalesCountTotal ?? 0);
  const unitsThisMonth =
    (stats?.affiliateSalesCountThisMonth ?? 0) + (stats?.campaignSalesCountThisMonth ?? 0);
  const stockTotal = variants.reduce((sum, v) => sum + (Number(v.inventory) || 0), 0);
  const prices = variants.map((v) => Number(v.price) || 0).filter((p) => p > 0);
  const priceRange =
    prices.length === 0
      ? null
      : Math.min(...prices) === Math.max(...prices)
        ? formatAmount(prices[0])
        : `${formatAmount(Math.min(...prices))} to ${formatAmount(Math.max(...prices))}`;
  const commission = variants[0]?.program ? formatCommission(variants[0].program) : null;

  const metrics: Metric[] = [
    {
      id: "product-units-sold",
      label: "Units sold",
      value: formatNumber(unitsTotal),
      change: "All time",
      series: monthlySeries,
      unit: "unit",
      labelFor: monthLabel,
    },
    {
      id: "product-affiliate-sales",
      label: "Affiliate sales",
      value: formatNumber(stats?.affiliateSalesCountTotal ?? 0),
      change: `${formatNumber(stats?.affiliateSalesCountThisMonth ?? 0)} this month`,
    },
    {
      id: "product-campaign-sales",
      label: "Campaign sales",
      value: formatNumber(stats?.campaignSalesCountTotal ?? 0),
      change: `${formatNumber(stats?.campaignSalesCountThisMonth ?? 0)} this month`,
    },
    {
      id: "product-this-month",
      label: "This month",
      value: formatNumber(unitsThisMonth),
      change:
        thisMonthKey && lastMonthKey
          ? describeMonthChange(monthlySeries[thisMonthKey], monthlySeries[lastMonthKey], formatNumber)
          : undefined,
    },
    {
      id: "product-stock",
      label: "In stock",
      value: formatNumber(stockTotal),
      change: hasNextPage ? "Loaded variants" : `${formatNumber(variants.length)} variants`,
    },
  ];

  const facts = [
    priceRange,
    `${formatNumber(variants.length)}${hasNextPage ? "+" : ""} ${variants.length === 1 ? "variant" : "variants"}`,
    commission ? `${commission} commission` : null,
  ].filter(Boolean);

  if (isLoading) return <InsightsSkeleton />;

  if (!product) {
    return (
      <div className="space-y-4">
        <PageHeader title="Product not found" closeTo="/brand/products/list" />
      </div>
    );
  }

  const tabs = [
    { value: "affiliate" as const, label: "Affiliate sales", count: affiliatePagination.total },
    { value: "campaign" as const, label: "Campaign sales", count: campaignPagination.total },
    ...(recurring?.connected
      ? [{ value: "recurring" as const, label: "Recurring", count: recurring.renewals.count }]
      : []),
    { value: "variants" as const, label: "Variants", count: variants.length },
  ];

  const recurringMetrics: Metric[] = recurring
    ? [
        {
          id: "product-active-subscriptions",
          label: "Active subscriptions",
          value: formatNumber(recurring.subscriptions.active),
          change:
            recurring.subscriptions.active + recurring.subscriptions.cancelled > 0
              ? `${formatNumber(recurring.subscriptions.retentionRate)}% retained`
              : "None attributed yet",
        },
        {
          id: "product-renewals",
          label: "Renewals",
          value: formatNumber(recurring.renewals.count),
          change:
            recurring.averageRenewalsPerSubscription > 0
              ? `${formatNumber(recurring.averageRenewalsPerSubscription)} per subscription`
              : undefined,
        },
        {
          id: "product-recurring-revenue",
          label: "Recurring revenue",
          value: formatAmount(recurring.renewals.revenue),
          change:
            recurring.averageOrder.recurring > 0
              ? `${formatAmount(recurring.averageOrder.recurring)} per renewal`
              : undefined,
        },
        {
          id: "product-recurring-commission",
          label: "Commission",
          value: formatAmount(recurring.renewals.commission),
          change:
            recurring.averageOrder.oneTime > 0
              ? `First orders average ${formatAmount(recurring.averageOrder.oneTime)}`
              : undefined,
        },
      ]
    : [];

  return (
    <div className="space-y-4">
      <PageHeader title={product.title} closeTo="/brand/products/list" />

      <MetricPanel title="Overview" metrics={metrics} columns="lg:grid-cols-5">
        <div className="flex items-center gap-4 px-5 pb-4">
          <ProductIdentity title={product.title} image={product.image} size="size-14 rounded-md" />
          {facts.length > 0 && (
            <span className="hidden text-xs text-muted-foreground sm:block">{facts.join(" · ")}</span>
          )}
        </div>
      </MetricPanel>

      <DashboardCard
        id="product-sales-card"
        title="Sales"
        bodyClassName="px-3 pb-3"
        fixed={
          <UnderlineTabs
            tabs={tabs}
            value={tab}
            onValueChange={setTab}
            className="mx-5 mb-2"
          />
        }
      >
        {tab === "affiliate" && (
          <>
            <DataTable
              columns={affiliateSalesColumns}
              minTableWidth="min-w-[40rem]"
              data={affiliateSales}
              isLoading={isLoadingAffiliateSales}
              getRowId={(row) => row.id}
              emptyText="No affiliate sales yet"
            />
            {affiliatePagination.total > 0 && (
              <DataTablePagination
                page={affiliatePagination.page}
                totalPages={affiliatePagination.totalPages}
                total={affiliatePagination.total}
                pageSize={affiliatePageSize}
                onPageChange={fetchAffiliateSales}
                onPageSizeChange={(size) => {
                  setAffiliatePageSize(size);
                  fetchAffiliateSales(1, size);
                }}
                pageSizeOptions={[10, 20, 50, 100]}
                noun="sales"
                className="-mx-3 -mb-3 mt-3"
              />
            )}
          </>
        )}
        {tab === "campaign" && (
          <>
            <DataTable
              columns={campaignSalesColumns}
              minTableWidth="min-w-[40rem]"
              data={campaignSales}
              isLoading={isLoadingCampaignSales}
              getRowId={(row) => row.id}
              emptyText="No campaign sales yet"
            />
            {campaignPagination.total > 0 && (
              <DataTablePagination
                page={campaignPagination.page}
                totalPages={campaignPagination.totalPages}
                total={campaignPagination.total}
                pageSize={campaignPageSize}
                onPageChange={fetchCampaignSales}
                onPageSizeChange={(size) => {
                  setCampaignPageSize(size);
                  fetchCampaignSales(1, size);
                }}
                pageSizeOptions={[10, 20, 50, 100]}
                noun="sales"
                className="-mx-3 -mb-3 mt-3"
              />
            )}
          </>
        )}
        {tab === "recurring" && recurring && (
          <>
            {/* The strip reaches the card edges like the one in the overview. */}
            <div className="-mx-3 mb-3 border-b border-border">
              <MetricStrip metrics={recurringMetrics} columns="lg:grid-cols-4" />
            </div>
            <DataTable
              columns={renewalColumns}
              minTableWidth="min-w-[40rem]"
              data={recurring.latestRenewals}
              isLoading={recurringQuery.isLoading}
              getRowId={(row) => row.saleId}
              emptyText="No renewals for this product yet"
            />
          </>
        )}
        {tab === "variants" && (
          <>
            <DataTable
              columns={variantColumns}
              data={variants}
              isLoading={isLoadingVariants && variants.length === 0}
              getRowId={(row) => row.id}
              emptyText="No variants for this product"
            />
            {hasNextPage && (
              <div className="mt-3 flex justify-end border-t border-border px-2 pt-3">
                <button
                  type="button"
                  onClick={handleLoadMoreVariants}
                  disabled={isLoadingVariants}
                  className="rounded-sm text-xs font-medium text-muted-foreground underline-offset-4 transition-colors hover:text-foreground hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50"
                >
                  {isLoadingVariants ? "Loading" : "Load more variants"}
                </button>
              </div>
            )}
          </>
        )}
      </DashboardCard>

      <Sheet open={marginVariant !== null} onOpenChange={(open) => !open && setMarginVariant(null)}>
        <SheetContent className="overflow-y-auto sm:max-w-md">
          <SheetHeader className="pr-6">
            <SheetTitle className="text-[15px]">Margin calculator</SheetTitle>
            <SheetDescription>{marginVariant?.title}</SheetDescription>
          </SheetHeader>
          {marginVariant && (
            <div className="mt-6">
              <MarginCalculator
                key={marginVariant.id}
                embedded
                productPrice={parseFloat(marginVariant.price)}
                initialAffiliateCommission={parseFloat(marginVariant.program?.commissionValue ?? "0")}
              />
            </div>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}
